import { useCallback, useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';

import AppActions from 'app/shared/flux/actions/AppActions';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';
import { getCurrentListingIndex } from 'app/shared/utils/listingsReducerUtils';
import { LISTINGS_FETCH_DEBOUNCE_MS, SIDEBAR_ELEMENT_ID } from '../constants';
import routeUtils from 'app/shared/utils/routeUtils';
import { getGlobalLogger } from '@zg-rentals/logger-base';

import type { AppThunkDispatch } from 'app/shared/flux/store/configureStore';
import type { AreaDetails } from 'app/shared/flux/reducers/area';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type { ParsedQueryObject } from 'app/shared/utils/queryUtils';

const logger = getGlobalLogger('hooks/useListingsFetchOrchestrator');

type UseListingsFetchOrchestratorParams = {
  area: AreaDetails;
  border: string | boolean | undefined;
  byCoordsListingCount: number;
  byCoordsListings: Array<ListingDetails>;
  currentListing: ListingDetails | null;
  dispatch: AppThunkDispatch;
  existsOutsideOfList: boolean;
  gmapLoaded: boolean;
  isAreaUrl: boolean;
  isInitialSsrPage: boolean;
  isLoggedIn: boolean;
  isMobile: boolean;
  isNearMeUrl: boolean;
  isPadOrBuildingUrl: boolean;
  isValidAreaPage: boolean;
  isValidSearchSlug: boolean;
  page: string | boolean | undefined;
  pathname: string;
  query: ParsedQueryObject;
  resolveListingArea: (prevListing: ListingDetails | null) => Promise<void>;
  showMap: boolean;
  slug: string;
};

type UseListingsFetchOrchestratorReturn = {
  fetchListings: () => Promise<void>;
  batchedFetchListings: () => Promise<void> | undefined;
  debouncedListingsHandler: () => void;
};

type PrevValues = {
  area: AreaDetails;
  border: string | boolean | undefined;
  gmapLoaded: boolean;
  isLoggedIn: boolean;
  page: string | boolean | undefined;
  pathname: string;
  query: ParsedQueryObject;
  slug: string;
};

/**
 * Orchestrates when and how listing fetches are triggered in response to prop/state changes.
 *
 * This was the core lifecycle logic of SplitMapTemplateClass — the most complex part of the
 * component. It replaces:
 *
 * - `componentDidMount` — initial fetch logic:
 *    • Area URLs (SRP): calls `batchedFetchListings` if not SSR'd
 *    • Listing URLs (HDP/BDP): resolves listing area, fetches listings on mobile SSR
 *
 * - `componentDidUpdate(prevProps)` — the main update handler that classified every render
 *    into one of several page-state buckets (SSR area page, CSR area page, HDP/BDP, etc.)
 *    and routed to the appropriate fetch strategy. Now handled by a depless `useEffect` that
 *    mirrors componentDidUpdate by comparing `prevValues` ref against current props.
 *
 * - `debouncedListingsHandler(func, prevProps)` — aggregated prop changes (area, slug, page,
 *    lat/lon/zoom, border, login state, gmap load) into a debounce window to batch multiple
 *    rapid changes into a single listing fetch. Now `debouncedListingsHandler()` (no args —
 *    reads prev values from ref).
 *
 * - `batchedFetchListings(prevProps)` — executed the debounced aggregate if non-empty.
 *    Now `batchedFetchListings()` (no args).
 *
 * - `fetchListings()` — dispatched `ListingEngineActions.fetchListings`, scrolled sidebar
 *    to top on desktop SRP, and updated the current listing index. Unchanged in behavior.
 *
 * - `componentWillUnmount` — cleared debounce timeout and reset isValidAreaPage/isValidSearchSlug.
 *    Now two separate cleanup `useEffect`s.
 */
export function useListingsFetchOrchestrator({
  area,
  border,
  byCoordsListingCount,
  byCoordsListings,
  currentListing,
  dispatch,
  existsOutsideOfList,
  gmapLoaded,
  isAreaUrl,
  isInitialSsrPage,
  isLoggedIn,
  isMobile,
  isNearMeUrl,
  isPadOrBuildingUrl,
  isValidAreaPage,
  isValidSearchSlug,
  page,
  pathname,
  query,
  resolveListingArea,
  showMap,
  slug,
}: UseListingsFetchOrchestratorParams): UseListingsFetchOrchestratorReturn {
  const debouncedAggregate = useRef<Array<string>>([]);
  const debouncedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevValues = useRef<PrevValues | null>(null);
  const prevListing = useRef<ListingDetails | null>(currentListing);
  const isFirstRender = useRef(true);
  const resolveListingAreaRef = useRef(resolveListingArea);
  resolveListingAreaRef.current = resolveListingArea;

  // Keep mutable refs for values needed inside fetchListings callback
  // so we always get the latest values when the debounced fetch fires
  const latestProps = useRef({
    byCoordsListingCount,
    byCoordsListings,
    currentListing,
    isAreaUrl,
    isMobile,
    showMap,
  });
  latestProps.current = {
    byCoordsListingCount,
    byCoordsListings,
    currentListing,
    isAreaUrl,
    isMobile,
    showMap,
  };

  const fetchListings = useCallback((): Promise<void> => {
    const { isAreaUrl: isArea, isMobile: mobile, showMap: mapShown, currentListing: listing } = latestProps.current;

    if (!mobile && isArea) {
      const sidebar = document.getElementById(SIDEBAR_ELEMENT_ID);
      if (sidebar && sidebar.scrollTo) {
        sidebar.scrollTo(0, 0);
      }
    }

    return dispatch(ListingEngineActions.fetchListings(mapShown))
      .then(() => {
        const { byCoordsListings: listings, byCoordsListingCount: count } = latestProps.current;

        if (listing) {
          if (count) {
            const indexInList = getCurrentListingIndex(listings, listing);
            dispatch(ListingEngineActions.setCurrentListingIndex(indexInList));
          }
        }
      })
      .catch((err: { to?: string }) => {
        if (err?.to) {
          logger?.info({
            errorLocation: 'SplitMapTemplate.fetchListings',
            message: `User has gone past area's total page count. Redirecting...`,
          });
          return window.router.redirectTo(err.to);
        }
        logger?.error({
          errorLocation: 'SplitMapTemplate.fetchListings',
          error: err,
        });
      });
  }, [dispatch]);

  const batchedFetchListings = useCallback((): Promise<void> | undefined => {
    logger?.debug('RUNNING FUNC - EXECUTE', debouncedAggregate.current);

    if (!isEmpty(debouncedAggregate.current)) {
      debouncedAggregate.current = [];

      if (debouncedTimeout.current) {
        clearTimeout(debouncedTimeout.current);
        debouncedTimeout.current = null;
      }

      return fetchListings();
    }
  }, [fetchListings]);

  const debouncedListingsHandler = useCallback((): void => {
    const prev = prevValues.current;
    if (!prev) {
      return;
    }

    const areaChanged = prev.area.resourceId !== area.resourceId;
    const hasSlugChanged = prev.slug !== slug;
    const hasPageChanged = prev.page !== page;
    const hasLatAndLon = routeUtils.hasLatAndLon(query);
    const gmapFinishedLoading = !prev.gmapLoaded && gmapLoaded;
    const hasUserLoggedIn = prev.isLoggedIn !== isLoggedIn;
    const borderChange = prev.border !== border;
    const latLonZoomChanged =
      hasLatAndLon &&
      prev.query &&
      query &&
      (prev.query.lat !== query.lat || prev.query.lon !== query.lon || prev.query.z !== query.z);

    if (!isMobile && !gmapLoaded) {
      return;
    }

    if (isMobile && !routeUtils.isAreaUrl(prev.pathname) && routeUtils.isAreaUrl(pathname)) {
      debouncedAggregate.current.push('mobileSrpLoad');
    }

    if (areaChanged && area.resourceId) {
      debouncedAggregate.current.push('area');
    }

    if (hasSlugChanged) {
      const isFilterDrivenSlugChange = routeUtils.hasFilterParams(query);
      if (!isFilterDrivenSlugChange) {
        debouncedAggregate.current.push('slug');
      }
    }

    if (hasPageChanged) {
      debouncedAggregate.current.push('pagination');
    }

    if (latLonZoomChanged) {
      debouncedAggregate.current.push('latLonZoom');
    }

    if (hasUserLoggedIn) {
      debouncedAggregate.current.push('login');
    }

    if (gmapFinishedLoading) {
      debouncedAggregate.current.push('gmapLoaded');
    }

    if (borderChange) {
      debouncedAggregate.current.push('border');
    }

    if (routeUtils.isPadOrBuildingUrl(pathname) && pathname !== prev.pathname) {
      debouncedAggregate.current.push('hdpChange');
    }

    if (!debouncedTimeout.current && !isEmpty(debouncedAggregate.current)) {
      dispatch(AppActions.setAppStoreBool('fetchListingsByCoordsComplete', false));

      debouncedTimeout.current = setTimeout(() => {
        batchedFetchListings();
      }, LISTINGS_FETCH_DEBOUNCE_MS);
    }
  }, [area, border, batchedFetchListings, dispatch, gmapLoaded, isLoggedIn, isMobile, page, pathname, query, slug]);

  // Handle componentDidUpdate logic via useEffect
  useEffect(() => {
    if (isFirstRender.current) {
      // componentDidMount logic
      isFirstRender.current = false;

      prevValues.current = {
        area,
        border,
        gmapLoaded,
        isLoggedIn,
        page,
        pathname,
        query,
        slug,
      };

      if (isAreaUrl && !isInitialSsrPage) {
        batchedFetchListings();
        return;
      }

      if (isPadOrBuildingUrl && currentListing) {
        if (isInitialSsrPage && isMobile) {
          fetchListings();
        }
        resolveListingAreaRef.current(null);
      }

      return;
    }

    // componentDidUpdate logic
    const prev = prevValues.current;
    if (!prev) {
      return;
    }

    const pathChanged = prev.pathname !== pathname;

    const isAreaPage = isAreaUrl || isNearMeUrl;
    const ssrAreaPageWithNoQuery = isAreaPage && isInitialSsrPage && isEmpty(query);
    const csrAreaPageWithNoQuery = isAreaPage && !isInitialSsrPage && isEmpty(query);
    const ssrAreaPageWithQuerySansGeoInfo =
      isAreaPage && isInitialSsrPage && !isEmpty(query) && !routeUtils.hasLatAndLon(query);
    const ssrAreaPageWithQuery = isAreaPage && isInitialSsrPage && !isEmpty(query);
    const csrAreaPageWithQuery = isAreaPage && !isInitialSsrPage && !isEmpty(query);

    if (ssrAreaPageWithNoQuery || ssrAreaPageWithQuerySansGeoInfo) {
      // Do nothing — listings already SSR'd
    } else if (csrAreaPageWithNoQuery || ssrAreaPageWithQuery || csrAreaPageWithQuery) {
      if (existsOutsideOfList) {
        dispatch(ListingEngineActions.setCurrentListingOutsideOfList(false));
      }
      if (pathChanged && !isValidAreaPage) {
        dispatch(AppActions.setAppStoreBool('isValidAreaPage', true));
      }
      if (pathChanged && !isValidSearchSlug) {
        dispatch(AppActions.setAppStoreBool('isValidSearchSlug', true));
      }
      debouncedListingsHandler();
    } else if (isPadOrBuildingUrl) {
      resolveListingAreaRef.current(prevListing.current).then(() => {
        debouncedListingsHandler();
      });
    }

    // Update prev values for next render
    prevValues.current = {
      area,
      border,
      gmapLoaded,
      isLoggedIn,
      page,
      pathname,
      query,
      slug,
    };
    prevListing.current = currentListing;
  }); // Intentionally no deps — runs on every render like componentDidUpdate

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debouncedTimeout.current) {
        clearTimeout(debouncedTimeout.current);
      }
    };
  }, []);

  // Cleanup isValidAreaPage/isValidSearchSlug on unmount (was in componentWillUnmount)
  useEffect(() => {
    return () => {
      if (!isValidAreaPage) {
        dispatch(AppActions.setAppStoreBool('isValidAreaPage', true));
      }
      if (!isValidSearchSlug) {
        dispatch(AppActions.setAppStoreBool('isValidSearchSlug', true));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { fetchListings, batchedFetchListings, debouncedListingsHandler };
}
